import { appLocalStore } from "../stores/appLocalStore";
import { roomStore } from "../stores/roomStore";
import { storageService } from "./storageService";
import { roomService } from "./roomService";
import { HostPeerService } from "./hostPeerService";
import { ParticipantPeerService } from "./participantPeerService";
import { signallingService } from "./signallingService";
import { webRTCService } from "./webrtcService";
import { async } from "../utils/common";
import { appConfig } from "../config";
import { appSyncStore } from "../stores/appSyncStore";
import { reaction } from "mobx";
import { gameLoaderService } from "./gameLoaderService";
import { consts } from "../consts";
import { urlOptions } from "../utils/urlOptions";

class AppService {

    public async initializeAsync() {

        appLocalStore.action(_ => {
            _.userId = storageService.getUserId()

            const savedUserName = storageService.getUserName();
            if (savedUserName) {
                _.userName = savedUserName;
            }

            const savedUserAvatar = storageService.getUserAvatar();
            if (savedUserAvatar) {
                _.userAvatar = savedUserAvatar;
            }
        });

        // Set up reaction to load game when activeGameId changes (Host & Participants)
        reaction(
            () => appSyncStore.gameId,
            (gameId) => {
                gameLoaderService.loadGame(gameId);
            },
            { fireImmediately: true }
        );

        reaction(
            () => gameLoaderService.game,
            (game) => {
                if (game) {
                    this._participantPeerService?.notifyGameLoaded(game.gameId);
                }
            }
        );

        reaction(
            () => appSyncStore.gameActive,
            (gameActive) => {
                if (gameActive) {
                    this.startGameReactionAsync();
                } else {
                    appLocalStore.navigateToPage("waiting");
                }
            }
        );

        reaction(
            () => appLocalStore.userAvatar,
            (userAvatar) => {
                if (userAvatar) {
                    console.log(`[AppService] User avatar updated. Length: ${(userAvatar.length / 1024).toFixed(2)} kb`);
                } else {
                    console.log('[AppService] User avatar cleared');
                }
            },
            { fireImmediately: true }
        );


        appLocalStore.navigateToPage("splash");
        await async.waitSecondsAsync(appConfig.splashScreenDurationSeconds);
        appLocalStore.navigateToPage("setup");
    }

    /**
     * Check if test mode is enabled via URL options
     * Returns true if URL contains 'test' in fragment
     */
    private isTestMode(): boolean {
        return urlOptions.test === true;
    }

    /**
     * Check if URL contains a number character for single-player testing
     */
    private isSinglePlayerTestMode(): boolean {
        const url = window.location.href;
        return /\d/.test(url);
    }

    /**
     * Generate a placeholder avatar with initials
     */
    private generateTestAvatar(name: string): string {
        const initials = name.split(' ').map(n => n[0]).join('').toUpperCase();
        const colors = [
            '#667eea', '#764ba2', '#f093fb', '#f5576c',
            '#4facfe', '#00f2fe', '#43e97b', '#38f9d7',
            '#fa709a', '#fee140', '#30cfd0', '#330867'
        ];
        const colorIndex = name.charCodeAt(0) % colors.length;
        const color = colors[colorIndex];

        // Create a simple SVG avatar
        const svg = `
            <svg width="80" height="80" xmlns="http://www.w3.org/2000/svg">
                <rect width="80" height="80" fill="${color}"/>
                <text x="40" y="40" font-family="Arial, sans-serif" font-size="32" 
                      fill="white" text-anchor="middle" dominant-baseline="central">
                    ${initials}
                </text>
            </svg>
        `;

        return `data:image/svg+xml;base64,${btoa(svg)}`;
    }

    /**
     * Add test users to the room for testing purposes
     */
    private addTestUsers() {
        const testNames = [
            'Alice Test', 'Bob Test', 'Charlie Test', 'Diana Test', 'Eve Test',
            'Frank Test', 'Grace Test', 'Henry Test', 'Iris Test', 'Jack Test',
            'Kate Test', 'Leo Test', 'Mary Test', 'Nick Test', 'Olivia Test',
            'Paul Test', 'Quinn Test', 'Rose Test', 'Sam Test', 'Tina Test'
        ];

        testNames.forEach((name, index) => {
            const userId = `test-user-${index + 1}`;
            appSyncStore.upsertUser({
                id: userId,
                name: name,
                avatar: this.generateTestAvatar(name),
                isTestUser: true
            });
        });
    }

    private _hostPeerService: HostPeerService | null = null;
    private _participantPeerService: ParticipantPeerService | null = null;

    // public navigateToPage(page: PageType) {
    //     appLocalStore.navigateToPage(page);
    // }

    // public addError(error: string) {
    //     appLocalStore.addError(error);
    // }

    // public dismissError(index: number) {
    //     appLocalStore.dismissError(index);
    // }

    public async hostRoom() {
        const gameId = 'werewolf';

        roomStore.setCreatingRoom(true);
        try {
            const userName = storageService.getUserName() || 'Host';
            const data = await roomService.createRoom(userName);

            if (data.success && data.joinCode && data.roomId && data.participantId) {

                appLocalStore.action(_ => _.isHost = true);

                roomStore.setRoomData({
                    joinCode: data.joinCode,
                    roomId: data.roomId,
                    participantId: data.participantId,
                });
                signallingService.initialize();

                appSyncStore.action(_ => {
                    _.hostId = appLocalStore.userId;
                    _.gameId = gameId;
                });

                appSyncStore.upsertUser({
                    id: appLocalStore.userId,
                    name: appLocalStore.userName ?? 'Host',
                    avatar: appLocalStore.userAvatar,
                    isTestUser: false
                });

                this._hostPeerService = new HostPeerService();

                // Add test users if in test mode
                if (this.isTestMode()) {
                    this.addTestUsers();
                }

                appLocalStore.navigateToPage("waiting");
            } else {
                appLocalStore.addErrorAction(data.error || 'Failed to create room');
            }
        } finally {
            roomStore.setCreatingRoom(false);
        }
    }

    public async joinRoom(joinCode: string) {
        if (!joinCode.trim()) {
            return;
        }

        appLocalStore.action(_ => _.isHost = false);

        // Clear previous error
        roomStore.clearJoinError();
        roomStore.setJoiningRoom(true);

        try {
            const userName = storageService.getUserName() || 'Player';
            const data = await roomService.joinRoom(joinCode, userName);

            if (data.success && data.roomId && data.participantId) {
                roomStore.setRoomData({
                    joinCode: joinCode.trim().toUpperCase(),
                    roomId: data.roomId,
                    participantId: data.participantId,
                });
                signallingService.initialize();
                this._participantPeerService = new ParticipantPeerService();
                
                // Save the join code for future use
                storageService.setJoinCode(joinCode.trim().toUpperCase());
                
                appLocalStore.navigateToPage("waiting");
            } else {
                roomStore.setJoinError(data.error || 'Failed to join room');
            }
        } catch (error) {
            console.error('Error joining room:', error);
            appLocalStore.addErrorAction('Failed to connect to server. Please try again.');
        } finally {
            roomStore.setJoiningRoom(false);
        }
    }

    // Only called by Host
    public async startGameOnHost() {
        appSyncStore.action(_ => _.gameActive = true);
    }

    private endGameOnHost() {
        appSyncStore.action(_ => _.gameActive = false);
    }

    private async startGameReactionAsync() {
        const game = gameLoaderService.game;
        if (!game) {
            console.error('[AppService] No game loaded to start.');
            return;
        }
        await game.gameService.startOnAllAsync();
        if (appLocalStore.isHost) {
            await game.gameService.startOnHostAsync();
            this.endGameOnHost();
        }
    }

    // TODO - Should this be here?
    sendGameMessageToHost(message: any) {
        this._participantPeerService?.sendToHost({
            type: consts.GAME_MESSAGE,
            data: message,
        });
    }

    /**
     * Leave the waiting room and return to lobby
     * - Participants: disconnect from host
     * - Host: disconnect from all participants
     */
    public leaveWaitingRoom() {
        if (appLocalStore.isHost) {
            // Host disconnects from all participants
            webRTCService.closeAll();
            this._hostPeerService = null;
        } else {
            // Participant disconnects from host
            const peerIds = webRTCService.getPeerIds();
            peerIds.forEach(peerId => webRTCService.closePeer(peerId));
            this._participantPeerService = null;
        }

        // Clear room state
        roomStore.clearRoom();
        
        // Clear app sync store
        appSyncStore.action(_ => {
            _.users.clear();
            _.hostId = null;
            _.gameId = null;
            _.gameActive = false;
        });

        // Reset host status
        appLocalStore.action(_ => _.isHost = null);

        // Navigate to lobby
        appLocalStore.navigateToPage("lobby");
    }

    /**
     * Disconnect a specific participant (Host only)
     * @param userId The ID of the user to disconnect
     */
    public disconnectParticipant(userId: string) {
        if (!appLocalStore.isHost) {
            console.error('Only host can disconnect participants');
            return;
        }

        if (!this._hostPeerService) {
            console.error('Host peer service not initialized');
            return;
        }

        // Use the host peer service to disconnect the participant
        const disconnected = this._hostPeerService.disconnectParticipant(userId);

        if (disconnected) {
            // Remove user from the store
            appSyncStore.removeUser(userId);
        } else {
            console.warn(`Could not find peer for user: ${userId}`);
        }
    }
}

export const appService = new AppService();

